/**
 * Reframes the scenes a Face Mapping save or a face re-run affects
 * (PLAT-12144), the way the reframe modal's Speakers tab does for one face.
 *
 * Plans only: returns each scene's new layout for the caller to commit in one
 * command. Hand-framed scenes, and scenes whose framing doesn't move, are left
 * alone.
 */

import { planSceneReframe, type SceneReframer } from './planSceneReframe';

import type { FaceDetection } from './types';
import type { IScene } from '@src/editor/models/layout/types';

export interface FaceMapSnapshot {
  /** transcript `speaker_label` → `face_label`s, as framing uses it. */
  speakerMap: Record<string, string[]>;
  faceDetections: FaceDetection[];
}

/** Speakers whose faces differ between the two maps, in either direction. */
export function changedSpeakers(
  before: Record<string, string[]>,
  after: Record<string, string[]>,
): Set<string> {
  const changed = new Set<string>();
  for (const speakerLabel of new Set([
    ...Object.keys(before),
    ...Object.keys(after),
  ])) {
    const was = [...(before[speakerLabel] ?? [])].sort().join('\n');
    const is = [...(after[speakerLabel] ?? [])].sort().join('\n');
    if (was !== is) changed.add(speakerLabel);
  }
  return changed;
}

export function planReframeAfterFaceMapChange({
  framer,
  before,
  after,
  faceDataReplaced,
  targetAspectRatio,
}: {
  framer: SceneReframer;
  before: FaceMapSnapshot;
  after: FaceMapSnapshot;
  /**
   * The face detections themselves were regenerated (Force Re-run), so every
   * speaker's framing may move, not only those whose mapping changed.
   */
  faceDataReplaced: boolean;
  targetAspectRatio?: number;
}): Array<{ sceneId: string; updates: Partial<IScene> }> {
  const speakerLabels = faceDataReplaced
    ? new Set(framer.timelineSpeakers())
    : changedSpeakers(before.speakerMap, after.speakerMap);
  if (speakerLabels.size === 0) return [];

  return planSceneReframe({
    framer,
    faceDetections: after.faceDetections,
    speakerMap: after.speakerMap,
    speakerLabels,
    targetAspectRatio,
    autoFramedOnlyFrom: before,
  });
}
